/*
 * File: GetSectionPeriods.ts
 * Author: Austin Robinson
 * Date Created: 8/5/2023
 * Copyright (c) DrivingDoc 2023
 */
import { createQuery } from 'react-query-kit';

import { privateApi } from 'api';

export type GetSectionPeriodsParams = {
  sectionId: string;
  page: number;
  pageSize: number;
};

export type SectionPeriod = {
  tenant_id: string;
  id: string;
  course_section_id: string;
  start: string;
  end: string;
  open_enrollment: string | null;
  close_enrollment: string | null;
  capacity: number | null;
  deleted: boolean;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  deleted_by: string | null;
  deleted_at: string | null;
  observation_capacity: number | null;
  waitlist_capacity: number | null;
  waitlist_observation_capacity: number | null;
  enrollment_count: string | null;
  waitlist_count: string | null;
  waitlist_observation_capacity_count: string | null;
  course_period_instructors:
    | {
        instructor_user_id: string;
        instructor: {
          tenant_id: string;
          user_id: string;
          calendar_color: string | null;
          created_at: string | null;
          updated_at: string | null;
          user: {
            tenant_id: string;
            id: string;
            title: string | null;
            first_name: string | null;
            middle_name: string | null;
            last_name: string | null;
            nickname: string | null;
            email: string | null;
            date_of_birth: string | null;
            sex: string | null;
            active: boolean;
            created_at: string | null;
            updated_at: string | null;
            created_by: string | null;
            updated_by: string | null;
            des_id: string | null;
          };
        };
      }[]
    | null;
};

export type GetSectionPeriodsResult = {
  rows: SectionPeriod[];
  count: number;
};

async function getSectionPeriods(params: GetSectionPeriodsParams): Promise<GetSectionPeriodsResult> {
  const { sectionId, ...queryParams } = params;

  // make api call
  const result = await privateApi.get<GetSectionPeriodsResult>(
    `${import.meta.env.VITE_API_BASE_URL}/courses/sections/${sectionId}/periods`,
    {
      params: queryParams,
    },
  );

  // return the data object
  return result.data;
}

const useGetSectionPeriods = createQuery<GetSectionPeriodsResult, GetSectionPeriodsParams, Error>({
  queryKey: ['section_periods'],
  fetcher: (variables) => getSectionPeriods(variables),
});

export { getSectionPeriods, useGetSectionPeriods };
